import { Size } from '@ff-os/shared-utils/types/Size';
import exifr from 'exifr';
import { loadDOMImage } from 'ff-canvas';
import { createCanvas } from './createCanvas';
import { doesBrowserAutoOrientImages } from './doesBrowserAutoOrientImages';
import { orientSize, resizeAndOrientImage } from './image-orientation';
import { mapExifOrientationToNumber } from './mapExifOrientationToNumber';
import { toBlob } from './toBlob';

export const normalizeImage = async (
    image: File,
    options: NormalizeImageOptions = {}
) => {

    const url = URL.createObjectURL(image);
    const size = await loadSizeFromImage(url);
    const newSize = resizeToTargetSize(size, 500);

    const browserAutoOrients = await doesBrowserAutoOrientImages();

    const exif = (options.readCapturedOn || !browserAutoOrients)
        ? await parseExifSafely(image)
        : undefined;

    const capturedOn = options.readCapturedOn ? readCapturedOn(exif) : undefined;

    if (browserAutoOrients) {
        return {
            url: await resizeImage(url, newSize),
            width: size.width,
            height: size.height,
            capturedOn
        };
    } else {

        const orientation = mapExifOrientationToNumber(exif?.['Orientation']) || 1;
        const orientedSize = orientSize(size, orientation);

        return {
            url: await resizeAndOrientImage(url, orientation, newSize),
            width: orientedSize.width,
            height: orientedSize.height,
            capturedOn
        };
    }
};

export type NormalizeImageOptions = {
    readCapturedOn?: boolean;
};

const EXIF_TAGS = ['Orientation', 'DateTimeOriginal', 'CreateDate'];

const parseExifSafely = async (image: File): Promise<Record<string, any> | undefined> => {
    try {
        return await exifr.parse(image, EXIF_TAGS);
    } catch {
        return undefined;
    }
};

const readCapturedOn = (exif: Record<string, any> | undefined): string | undefined => {
    const value = exif?.['DateTimeOriginal'] ?? exif?.['CreateDate'];
    if (value instanceof Date && !isNaN(value.getTime())) {
        return toCalendarDate(value);
    }
    return undefined;
};

const pad = (n: number) => String(n).padStart(2, '0');

const toCalendarDate = (date: Date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const loadSizeFromImage = async (imageUrl: string) => {
    const element = await loadDOMImage(imageUrl, 'anonymous');
    return {
        width: element.width,
        height: element.height
    };
};

const resizeToTargetSize = (
    size: Size,
    targetSize: number
) => {

    const width = size.width > size.height
        ? targetSize * (size.width / size.height)
        : targetSize;

    const height = size.width < size.height
        ? targetSize * (size.height / size.width)
        : targetSize;

    return {
        width,
        height
    };
};

const resizeImage = async (imageUrl: string, size: Size) => {

    const canvas = createCanvas(size.width, size.height);
    const ctx = canvas.getContext('2d')!;

    const image = await loadDOMImage(imageUrl, 'anonymous');

    ctx.clearRect(0, 0, size.width, size.height);
    ctx.drawImage(image, 0, 0, size.width, size.height);

    URL.revokeObjectURL(imageUrl);

    return URL.createObjectURL(await toBlob(canvas));
};